import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import { Box, Container, Typography, IconButton, TextField, CircularProgress, Stack, Skeleton } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "notistack";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import GetCookieByName from "../../hooks/GetCookie";
import { usePlanSummary, usePlanInstagramConversations, usePlanInstagramThread, useSendPlanInstagramMessage } from "../../hooks/usePlanSummary";
import { formatRelativeTime, formatTime, groupMessagesByDate, getNumberColor } from "../whatsapp/chatUiHelpers";

// The "View Instagram Chat" destination — every Instagram DM thread (across all our connected
// Instagram accounts) with an influencer on THIS plan, in the same conversation-list + thread
// layout as PlanWhatsAppPage.jsx, backed by the plan-scoped, org-member-gated endpoints (see
// usePlanSummary.js). Messages here are keyed by igMessageId and arrive on the 'new-message'
// socket event, so the cache helpers below are local rather than the WhatsApp (waMessageId) ones.

const INSTAGRAM_PINK = "#E1306C";

function upsertThreadMessage(oldData, conversationId, message) {
  if (!oldData?.conversation || oldData.conversation.conversationId !== conversationId) return oldData;
  if (oldData.conversation.messages.some((m) => m.igMessageId === message.igMessageId)) return oldData;
  return {
    ...oldData,
    conversation: {
      ...oldData.conversation,
      lastMessageAt: message.sentAt,
      messages: [...oldData.conversation.messages, message],
    },
  };
}

function bumpConversationPreview(oldData, conversationId, message) {
  if (!oldData?.conversations) return oldData;
  const idx = oldData.conversations.findIndex((c) => c.conversationId === conversationId);
  if (idx === -1) return oldData;
  const updated = { ...oldData.conversations[idx], lastMessageAt: message.sentAt, messages: [message] };
  const rest = oldData.conversations.filter((_, i) => i !== idx);
  return { ...oldData, conversations: [updated, ...rest] };
}

const creatorLabel = (conv) => (conv?.influencerUsername ? `@${conv.influencerUsername}` : "Unknown creator");

function ConversationRow({ conv, isSelected, onSelect }) {
  const color = getNumberColor(conv.ourIgUserId || conv.ourAccount);
  const lastMsg = (conv.messages || [])[0];
  const needsReply = lastMsg && !lastMsg.isFromUs;
  const preview = lastMsg ? `${lastMsg.isFromUs ? "You: " : ""}${lastMsg.text || "[attachment]"}` : "No messages yet";

  return (
    <Box
      onClick={() => onSelect(conv.conversationId)}
      sx={{
        px: 2,
        py: 1.5,
        cursor: "pointer",
        borderLeft: isSelected ? `3px solid ${INSTAGRAM_PINK}` : "3px solid transparent",
        bgcolor: isSelected ? "rgba(225,48,108,0.06)" : "transparent",
        borderBottom: "1px solid rgba(0,0,0,0.05)",
        transition: "background-color 0.15s ease",
        "&:hover": { bgcolor: isSelected ? "rgba(225,48,108,0.06)" : "rgba(0,0,0,0.02)" },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: "Quicksand",
            fontWeight: needsReply ? 800 : 700,
            fontSize: 13.5,
            color: "#1C1C2E",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {creatorLabel(conv)}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: "#9CA3AF", flexShrink: 0 }}>{formatRelativeTime(conv.lastMessageAt)}</Typography>
      </Stack>
      <Typography sx={{ fontFamily: "Quicksand", fontSize: 11, color, fontWeight: 700, pl: 2.25, mt: 0.1 }}>
        via @{conv.ourAccount}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pl: 2.25, mt: 0.4 }}>
        {needsReply && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: INSTAGRAM_PINK, flexShrink: 0 }} />}
        <Typography
          sx={{
            fontFamily: "Quicksand",
            fontSize: 12,
            color: needsReply ? "#374151" : "#9CA3AF",
            fontWeight: needsReply ? 600 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {preview}
        </Typography>
      </Stack>
    </Box>
  );
}

function MessageBubble({ message, accentColor }) {
  const isUs = message.isFromUs;
  return (
    <Box sx={{ display: "flex", justifyContent: isUs ? "flex-end" : "flex-start", mb: 0.75 }}>
      <Box
        sx={{
          maxWidth: { xs: "85%", sm: "75%", md: "68%" },
          background: isUs ? `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}CC 100%)` : "rgba(255,255,255,0.97)",
          color: isUs ? "#fff" : "#1C1C2E",
          borderRadius: isUs ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          px: 2,
          py: 1.25,
          boxShadow: isUs ? `0 4px 16px ${accentColor}40` : "0 2px 8px rgba(0,0,0,0.07)",
          border: isUs ? "none" : "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {message.text ? (
          <Typography sx={{ fontSize: 13.5, fontFamily: "Quicksand", fontWeight: 600, lineHeight: 1.55, wordBreak: "break-word" }}>
            {message.text}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, fontFamily: "Quicksand", fontStyle: "italic", opacity: 0.55 }}>[attachment]</Typography>
        )}
        <Typography sx={{ fontSize: 10, fontFamily: "Quicksand", color: isUs ? "rgba(255,255,255,0.65)" : "#9CA3AF", textAlign: "right", mt: 0.5 }}>
          {formatTime(message.sentAt)}
        </Typography>
      </Box>
    </Box>
  );
}

function ThreadComposer({ conversation, onSend, sending }) {
  const [text, setText] = useState("");
  const accentColor = getNumberColor(conversation?.ourIgUserId || conversation?.ourAccount);
  const noToken = conversation?.hasActiveToken === false;
  const windowClosed = conversation?.messagingWindow === "closed";
  const disabled = noToken || windowClosed;

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled || sending) return;
    setText("");
    onSend(trimmed);
  };

  return (
    <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", background: "rgba(255,255,255,0.9)", flexShrink: 0 }}>
      {disabled && (
        <Typography sx={{ px: 2.5, py: 1.5, fontSize: 12, fontFamily: "Quicksand", color: "#9CA3AF", fontStyle: "italic" }}>
          {noToken
            ? `Sending is unavailable — no active Instagram token for @${conversation?.ourAccount}.`
            : "This creator hasn't messaged in over 7 days — Instagram doesn't allow replying until they message again."}
        </Typography>
      )}
      {!disabled && (
        <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Type a message…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "18px", fontFamily: "Quicksand", fontSize: 13.5, background: "rgba(0,0,0,0.02)" } }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!text.trim() || sending}
            sx={{
              bgcolor: text.trim() ? accentColor : "rgba(0,0,0,0.06)",
              color: text.trim() ? "#fff" : "#9CA3AF",
              "&:hover": { bgcolor: text.trim() ? accentColor : "rgba(0,0,0,0.06)", opacity: 0.9 },
              width: 38,
              height: 38,
            }}
          >
            {sending ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SendRoundedIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}

function ThreadPanel({ planId, conversationId, onSend, sending }) {
  const { data, isLoading } = usePlanInstagramThread(planId, conversationId);
  const scrollRef = useRef(null);
  const conversation = data?.conversation;
  const accentColor = getNumberColor(conversation?.ourIgUserId || conversation?.ourAccount);
  const messages = conversation?.messages || [];
  const grouped = groupMessagesByDate(messages);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversationId, messages.length]);

  if (!conversationId) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13.5 }}>Select a conversation to view it</Typography>
      </Box>
    );
  }

  if (isLoading || !conversation) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5, p: 2.5 }}>
        {[...Array(6)].map((_, i) => (
          <Box key={i} sx={{ display: "flex", justifyContent: i % 2 === 0 ? "flex-end" : "flex-start" }}>
            <Skeleton variant="rounded" width={`${40 + (i % 3) * 15}%`} height={44} sx={{ borderRadius: i % 2 === 0 ? "18px 18px 4px 18px" : "18px 18px 18px 4px" }} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: "rgba(255,255,255,0.85)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: accentColor, boxShadow: `0 0 0 3px ${accentColor}30` }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 14, color: "#1C1C2E" }}>{creatorLabel(conversation)}</Typography>
          <Typography sx={{ fontFamily: "Quicksand", fontSize: 10.5, color: accentColor, fontWeight: 700 }}>via @{conversation.ourAccount}</Typography>
        </Box>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2.5,
          py: 2,
          background: "linear-gradient(145deg, rgba(255,232,240,0.4) 0%, rgba(237,241,255,0.3) 100%)",
        }}
      >
        {Object.entries(grouped).map(([dateLabel, msgs]) => (
          <Box key={dateLabel}>
            <Box sx={{ display: "flex", alignItems: "center", my: 2, gap: 1.5 }}>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: "Quicksand", color: "#9CA3AF", px: 1.5, py: 0.4, bgcolor: "rgba(0,0,0,0.04)", borderRadius: 99, letterSpacing: 0.5 }}>
                {dateLabel}
              </Typography>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
            </Box>
            {msgs.map((msg, i) => (
              <MessageBubble key={msg.igMessageId || `msg-${dateLabel}-${i}`} message={msg} accentColor={accentColor} />
            ))}
          </Box>
        ))}
      </Box>

      <ThreadComposer conversation={conversation} onSend={onSend} sending={sending} />
    </Box>
  );
}

function PlanInstagramPage() {
  const { id: planId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const socketRef = useRef(null);
  const joinedRoomsRef = useRef(new Set());
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [sending, setSending] = useState(false);

  const { data: planData } = usePlanSummary(planId);
  const { data, isLoading } = usePlanInstagramConversations(planId);
  const conversations = useMemo(() => data?.conversations || [], [data]);
  const sendMutation = useSendPlanInstagramMessage(planId);

  useEffect(() => {
    if (!selectedConversationId && conversations.length) {
      setSelectedConversationId(conversations[0].conversationId);
    }
  }, [conversations, selectedConversationId]);

  // Live updates over the shared socket server. The server only lets internal accounts
  // connect, so for other org members this never connects and the queries' own
  // refetchInterval (see usePlanSummary.js) keeps the page current instead.
  useEffect(() => {
    let authToken;
    try {
      authToken = JSON.parse(GetCookieByName("auth"))["token"];
    } catch (e) {
      return undefined;
    }

    const socket = io(new URL(process.env.REACT_APP_API_URL).origin, { auth: { token: authToken }, reconnection: false });
    socketRef.current = socket;
    joinedRoomsRef.current = new Set();
    socket.on("connect_error", () => {});

    socket.on("new-message", ({ conversationId, message }) => {
      queryClient.setQueryData(["plan-instagram-thread", planId, conversationId], (old) => upsertThreadMessage(old, conversationId, message));
      queryClient.setQueryData(["plan-instagram-conversations", planId], (old) => bumpConversationPreview(old, conversationId, message));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [planId, queryClient]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const toJoin = conversations.map((c) => c.conversationId).filter((cid) => cid && !joinedRoomsRef.current.has(cid));
    if (!toJoin.length) return;

    const doJoin = () => {
      toJoin.forEach((cid) => {
        socket.emit("join", cid);
        joinedRoomsRef.current.add(cid);
      });
    };
    if (socket.connected) doJoin();
    else socket.once("connect", doJoin);
  }, [conversations]);

  const handleSend = async (text) => {
    setSending(true);
    try {
      const res = await sendMutation.mutateAsync({ conversationId: selectedConversationId, text });
      if (!res?.success) throw new Error(res?.message || "Failed to send message");
      queryClient.setQueryData(["plan-instagram-thread", planId, selectedConversationId], (old) => upsertThreadMessage(old, selectedConversationId, res.message));
      queryClient.setQueryData(["plan-instagram-conversations", planId], (old) => bumpConversationPreview(old, selectedConversationId, res.message));
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || error.message || "Failed to send message", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <DashboardHeaderSection hideSearch />
      <Container sx={{ marginTop: 8, pb: 6, pl: { xs: 2, sm: 3, lg: "108px" }, pr: { xs: 2, sm: 3 } }} maxWidth="xl">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate(`/plans-summary/${planId}`)} size="small" sx={{ "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 1 }}>
              <InstagramIcon sx={{ color: INSTAGRAM_PINK }} /> {planData?.plan?.name ? `${planData.plan.name} — Instagram` : "Plan Instagram"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            border: "1px solid rgba(0,0,0,0.07)",
            borderRadius: "16px",
            overflow: "hidden",
            height: "calc(100vh - 260px)",
            minHeight: 520,
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: 300, md: 340 }, flexShrink: 0, borderRight: "1px solid rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {isLoading ? (
                <Box sx={{ p: 2 }}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={54} sx={{ mb: 1, borderRadius: "10px" }} />
                  ))}
                </Box>
              ) : conversations.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13 }}>
                    No Instagram conversations yet for this plan's influencers.
                  </Typography>
                </Box>
              ) : (
                conversations.map((conv) => (
                  <ConversationRow
                    key={conv.conversationId}
                    conv={conv}
                    isSelected={conv.conversationId === selectedConversationId}
                    onSelect={setSelectedConversationId}
                  />
                ))
              )}
            </Box>
          </Box>

          <ThreadPanel planId={planId} conversationId={selectedConversationId} onSend={handleSend} sending={sending} />
        </Box>
      </Container>
    </Box>
  );
}

export default PlanInstagramPage;
